import { formatPhoneInput } from '../utils/formatters';

export function TextField({ label, value, onChange, error, type = 'text', full, ...rest }) {
  return (
    <div
      className={`field${full ? ' full' : ''}${error ? ' has-error' : ''}`}
    >
      <label>{label}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        {...rest}
      />
      {error && <span className="error">{error}</span>}
    </div>
  );
}

/**
 * Phone-number field that auto-formats as the user types. Every keystroke
 * (including pastes) is funneled through formatPhoneInput, so callers
 * receive — and get to save — the canonical display string:
 *   • "(XXX) XXX-XXXX" for US/Canada (default)
 *   • "+CC CC CCCC CCCC" when the user starts with "+" (international)
 *   • "<above> ext. NNN" when `allowExtension` is set and the user
 *     typed `x`, `ext`, `ext.`, or `#` after the main number
 *
 * `allowExtension` is opt-in so cell-phone fields don't accidentally
 * accept extensions — most cells don't have them, and a typo'd `x`
 * in a cell field is safer to silently strip than to render as a
 * dangling " ext. " marker. No validation either way; partial input
 * renders intact while the user types.
 */
export function PhoneField({ value, onChange, allowExtension = false, ...rest }) {
  // Placeholder hints at both shapes. International is permissive —
  // anything starting with `+` is treated as international and the
  // user's own spacing is preserved (so "+44 20 7946 0958",
  // "+1 555 123 4567", etc. all work as typed).
  const placeholder = allowExtension
    ? '(XXX) XXX-XXXX ext. 123  or  +CC ... ext. 123'
    : '(XXX) XXX-XXXX  or  +CC ...';
  return (
    <TextField
      type="tel"
      value={value}
      onChange={(v) => onChange?.(formatPhoneInput(v, { allowExtension }))}
      placeholder={placeholder}
      {...rest}
    />
  );
}

export function TextArea({ label, value, onChange, rows = 3, full = true, error, ...rest }) {
  return (
    <div
      className={`field${full ? ' full' : ''}${error ? ' has-error' : ''}`}
    >
      {label && <label>{label}</label>}
      <textarea
        rows={rows}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        {...rest}
      />
      {error && <span className="error">{error}</span>}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  full,
  error,
  placeholder,
  ...rest
}) {
  return (
    <div
      className={`field${full ? ' full' : ''}${error ? ' has-error' : ''}`}
    >
      <label>{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        {...rest}
      >
        <option value="">{placeholder || '— select —'}</option>
        {options.map((o) =>
          typeof o === 'string' ? (
            <option key={o} value={o}>{o}</option>
          ) : (
            <option key={o.value} value={o.value}>{o.label}</option>
          ),
        )}
      </select>
      {error && <span className="error">{error}</span>}
    </div>
  );
}
